import React, { useState, useMemo, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { MapPin, Bed, Bath, Wifi, Car, Star, Filter, Grid, List, ChevronDown, Plus, Building2, CheckCircle, Box, Link, ChevronUp, X, Home, DollarSign, TrendingUp, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import Map3DView from '../components/Map3DView';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom marker icon for selected property
const selectedIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Map controller to update view when search location changes
const MapController = ({ searchLocation }) => {
  const map = useMap();
  
  // City coordinates mapping
  const cityCoords = {
    'Dallas': { lat: 32.7767, lng: -96.7970 },
    'Houston': { lat: 29.7604, lng: -95.3698 },
    'Austin': { lat: 30.2672, lng: -97.7431 },
    'San Antonio': { lat: 29.4241, lng: -98.4936 },
    'Fort Worth': { lat: 32.7555, lng: -97.3308 },
    'Los Angeles': { lat: 34.0522, lng: -118.2437 },
    'New York': { lat: 40.7128, lng: -74.0060 },
    'Miami': { lat: 25.7617, lng: -80.1918 },
    'Chicago': { lat: 41.8781, lng: -87.6298 },
    'Phoenix': { lat: 33.4484, lng: -112.0740 },
  };
  
  useEffect(() => {
    if (searchLocation && map) {
      try {
        const cityName = searchLocation.split(',')[0].trim();
        const coords = cityCoords[cityName] || cityCoords['Dallas'];
        // Wait for map to be ready before flying
        setTimeout(() => {
          if (map && map._loaded) {
            map.flyTo([coords.lat, coords.lng], 11, { duration: 1.5 });
          }
        }, 100);
      } catch (error) {
        console.warn('MapController: Error flying to location', error);
      }
    }
  }, [searchLocation, map]);
  
  return null;
};

// Platform icons for real estate listings
const ListingPlatformIcon = ({ platform, size = 20 }) => {
  // Use Building2 for commercial, Home for residential
  if (platform === 'loopnet') return <Building2 size={size} className="text-blue-400" />;
  if (platform === 'zillow') return <Home size={size} className="text-blue-500" />;
  if (platform === 'airbnb') return <Home size={size} className="text-red-400" />;
  if (platform === 'realtor') return <Home size={size} className="text-red-500" />;
  if (platform === 'redfin') return <Home size={size} className="text-orange-500" />;
  if (platform === 'crexi') return <Building2 size={size} className="text-purple-400" />;
  return <Building2 size={size} />;
};

// ============== LISTING CONNECTIONS PANEL ==============
const ListingConnectionsPanel = ({ connections, onConnect, onDisconnect, isExpanded, onToggle, listings, onRefresh, isLoading, searchLocation, setSearchLocation, onSearchLocationChange }) => {
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [credentials, setCredentials] = useState({});
  const [localSearchInput, setLocalSearchInput] = useState(searchLocation || 'Dallas, TX');

  // Sync localSearchInput when searchLocation prop changes
  useEffect(() => {
    if (searchLocation && searchLocation !== localSearchInput) {
      setLocalSearchInput(searchLocation);
    }
  }, [searchLocation]);

  const platforms = [
    { id: 'loopnet', name: 'LoopNet', description: 'Commercial real estate listings', color: 'blue', type: 'commercial', fields: ['api_key', 'search_location'] },
    { id: 'zillow', name: 'Zillow', description: 'Residential sales and rentals', color: 'blue', type: 'residential', fields: ['api_key', 'search_location'] },
    { id: 'airbnb', name: 'Airbnb', description: 'Short-term vacation rentals', color: 'red', type: 'vacation', fields: ['api_key', 'search_location'] },
    { id: 'realtor', name: 'Realtor.com', description: 'MLS listings nationwide', color: 'red', type: 'residential', fields: ['api_key', 'search_location'] },
    { id: 'redfin', name: 'Redfin', description: 'Real estate brokerage listings', color: 'orange', type: 'residential', fields: ['api_key', 'search_location'] },
    { id: 'crexi', name: 'Crexi', description: 'Commercial property marketplace', color: 'purple', type: 'commercial', fields: ['api_key', 'search_location'] }
  ];

  const handleConnect = (platform) => {
    setSelectedPlatform(platform);
    setCredentials({});
    setShowConnectModal(true);
  };

  const handleSaveConnection = () => {
    if (!selectedPlatform) return;
    
    const missingFields = selectedPlatform.fields.filter(f => !credentials[f]?.trim());
    if (missingFields.length > 0) {
      toast.error(`Please fill in all required fields`);
      return;
    }

    onConnect(selectedPlatform.id, credentials);
    setShowConnectModal(false);
    setSelectedPlatform(null);
    setCredentials({});
    toast.success(`${selectedPlatform.name} connected successfully!`);
  };

  const connectedCount = connections.filter(c => c.connected).length;

  const handleSearchLocation = () => {
    if (localSearchInput.trim()) {
      onSearchLocationChange(localSearchInput.trim());
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchLocation();
    }
  };

  return (
    <>
      {/* Collapsible Panel */}
      <div className="bg-black border border-gray-400 rounded-xl mb-4 overflow-hidden">
        <div
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-900 transition-colors cursor-pointer"
          data-testid="listing-connections-toggle"
        >
          <div className="flex items-center gap-3 flex-1" onClick={onToggle}>
            <div className="p-2 bg-cyan-500/20 rounded-lg">
              <Link className="text-cyan-400" size={18} />
            </div>
            <div className="text-left">
              <h3 className="text-white font-semibold text-sm">Listing Sources</h3>
              <p className="text-xs text-gray-400">{connectedCount} of {platforms.length} connected</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {connectedCount > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); onRefresh(); }}
                disabled={isLoading}
                className="px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded-lg text-xs hover:bg-cyan-500/30 transition-colors flex items-center gap-1"
              >
                {isLoading ? (
                  <span className="animate-spin">↻</span>
                ) : (
                  <>↻ Refresh</>
                )}
              </button>
            )}
            <div onClick={onToggle} className="cursor-pointer">
              {isExpanded ? <ChevronUp className="text-gray-400" size={18} /> : <ChevronDown className="text-gray-400" size={18} />}
            </div>
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="px-4 pb-4 border-t border-gray-600">
            {/* Location Search Bar */}
            <div className="mt-4 mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <MapPin size={14} className="inline mr-1 text-cyan-400" />
                Search Location
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={localSearchInput}
                  onChange={(e) => setLocalSearchInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter city, state (e.g., Miami, FL)"
                  className="flex-1 px-4 py-2.5 bg-black border border-gray-500 rounded-lg text-white focus:border-cyan-500 focus:outline-none text-sm"
                  data-testid="location-search-input"
                />
                <button
                  onClick={handleSearchLocation}
                  disabled={isLoading || connectedCount === 0}
                  className="px-4 py-2.5 bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg flex items-center gap-2 transition-colors text-sm font-medium"
                  data-testid="location-search-btn"
                >
                  {isLoading ? (
                    <span className="animate-spin">↻</span>
                  ) : (
                    <>
                      <MapPin size={16} />
                      Search
                    </>
                  )}
                </button>
              </div>
              {connectedCount === 0 && (
                <p className="text-xs text-gray-500 mt-2">Connect a platform first to search listings</p>
              )}
              {connectedCount > 0 && searchLocation && (
                <p className="text-xs text-cyan-400 mt-2">Currently showing listings in: {searchLocation}</p>
              )}
            </div>

            {/* Platform Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-4">
              {platforms.map(platform => {
                const connection = connections.find(c => c.platform === platform.id);
                const isConnected = connection?.connected;
                const platformListings = listings.filter(l => l.source === platform.id);

                return (
                  <div
                    key={platform.id}
                    className={`p-3 rounded-xl border transition-all duration-200 ${
                      isConnected 
                        ? 'bg-green-500/10 border-green-500/30 hover:border-green-500/50' 
                        : 'bg-black border-gray-600 hover:border-gray-400'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <ListingPlatformIcon platform={platform.id} size={20} />
                      {isConnected && <CheckCircle className="text-green-400" size={14} />}
                    </div>
                    <h4 className="text-white font-medium text-sm">{platform.name}</h4>
                    <p className="text-xs text-gray-500 mt-0.5">{platform.type}</p>
                    {isConnected && platformListings.length > 0 && (
                      <p className="text-xs text-cyan-400 mt-1">{platformListings.length} listings</p>
                    )}
                    <button
                      onClick={() => isConnected ? onDisconnect(platform.id) : handleConnect(platform)}
                      className={`w-full mt-2 px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                        isConnected
                          ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                          : 'bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30'
                      }`}
                      data-testid={`connect-${platform.id}`}
                    >
                      {isConnected ? 'Disconnect' : 'Connect'}
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Connected Listings Summary */}
            {listings.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="text-green-400" size={16} />
                    <span className="text-green-400 font-medium text-sm">{listings.length} listings loaded</span>
                  </div>
                  <span className="text-xs text-gray-400">Scroll down to view in property list</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Connection Modal */}
      {showConnectModal && selectedPlatform && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[9999] p-4" onClick={() => setShowConnectModal(false)}>
          <div className="bg-black border border-gray-400 rounded-xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="listing-connect-modal">
            <div className="p-5 border-b border-gray-600">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-cyan-500/20">
                  <ListingPlatformIcon platform={selectedPlatform.id} size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Connect {selectedPlatform.name}</h3>
                  <p className="text-sm text-gray-400">{selectedPlatform.description}</p>
                </div>
              </div>
            </div>

            <div className="p-5 space-y-4">
              {selectedPlatform.fields.map(field => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-300 mb-1 capitalize">
                    {field.replace(/_/g, ' ')} *
                  </label>
                  <input
                    type={field.includes('key') || field.includes('token') ? 'password' : 'text'}
                    value={credentials[field] || ''}
                    onChange={e => setCredentials({...credentials, [field]: e.target.value})}
                    className="w-full px-4 py-2 bg-black border border-gray-500 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                    placeholder={field === 'search_location' ? 'e.g., Dallas, TX' : `Enter ${field.replace(/_/g, ' ')}...`}
                  />
                </div>
              ))}

              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <p className="text-xs text-blue-400">
                  <strong>Note:</strong> We'll fetch {selectedPlatform.type} listings from {selectedPlatform.name} matching your search criteria.
                </p>
              </div>
            </div>

            <div className="p-5 border-t border-gray-600 flex gap-3">
              <button
                onClick={() => setShowConnectModal(false)}
                className="flex-1 px-4 py-2.5 bg-black border border-gray-500 hover:bg-gray-900 text-gray-300 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveConnection}
                className="flex-1 px-4 py-2.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg flex items-center justify-center gap-2 transition-colors"
                data-testid="save-listing-connection-btn"
              >
                <Link size={16} />
                Connect
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const RealEstateManagingPartnerProperties = () => {
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [hoveredProperty, setHoveredProperty] = useState(null);
  const [viewType, setViewType] = useState('list'); // 'list' or 'grid'
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [locationFilter, setLocationFilter] = useState('All Locations');
  const [showMapView, setShowMapView] = useState(true);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  
  // Track window resize to detect mobile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const openModal = (modalType) => {
    console.log('Opening modal:', modalType);
  };
  const [detailViewProperty, setDetailViewProperty] = useState(null);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false); // For detailed property view
  const [activeCategory, setActiveCategory] = useState('All');
  const [map3DMode, setMap3DMode] = useState(false); // Toggle between 2D and 3D map

  // Listing connections state
  const [listingConnectionsExpanded, setListingConnectionsExpanded] = useState(false);
  const [listingConnections, setListingConnections] = useState(() => {
    const saved = localStorage.getItem('listing_connections');
    return saved ? JSON.parse(saved) : [];
  });
  const [externalListings, setExternalListings] = useState([]);
  const [isLoadingListings, setIsLoadingListings] = useState(false);
  const [searchLocation, setSearchLocation] = useState(() => {
    const saved = localStorage.getItem('listing_search_location');
    return saved || 'Dallas, TX';
  });

  // Save listing connections and search location to localStorage
  useEffect(() => {
    localStorage.setItem('listing_connections', JSON.stringify(listingConnections));
  }, [listingConnections]);

  useEffect(() => {
    localStorage.setItem('listing_search_location', searchLocation);
  }, [searchLocation]);

  // Handle connecting a listing platform
  const handleConnectListingPlatform = (platformId, credentials) => {
    // Update searchLocation state if the credentials include a search_location
    if (credentials?.search_location) {
      setSearchLocation(credentials.search_location);
    }
    
    setListingConnections(prev => {
      const existing = prev.find(c => c.platform === platformId);
      if (existing) {
        return prev.map(c => c.platform === platformId 
          ? { ...c, connected: true, credentials, connectedAt: new Date().toISOString() }
          : c
        );
      }
      return [...prev, { 
        platform: platformId, 
        connected: true, 
        credentials, 
        connectedAt: new Date().toISOString() 
      }];
    });
    // Fetch listings after connecting
    fetchExternalListings(platformId, credentials);
  };

  // Handle disconnecting a listing platform
  const handleDisconnectListingPlatform = (platformId) => {
    if (!window.confirm('Are you sure you want to disconnect this platform?')) return;
    setListingConnections(prev => prev.map(c => 
      c.platform === platformId ? { ...c, connected: false, credentials: null } : c
    ));
    setExternalListings(prev => prev.filter(l => l.source !== platformId));
    toast.success('Platform disconnected');
  };

  // Mock fetch external listings (would be real API calls in production)
  // Generates realistic listings based on platform and search location
  const fetchExternalListings = async (platformId, credentials, locationOverride = null) => {
    setIsLoadingListings(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Use locationOverride if provided, otherwise use credentials.search_location or current searchLocation state
    const locationToSearch = locationOverride || credentials?.search_location || searchLocation || 'Dallas, TX';
    const cityName = locationToSearch.split(',')[0].trim();
    const statePart = locationToSearch.split(',')[1]?.trim() || 'TX';  // Extract state from location string
    
    // Property images by type
    const commercialImages = [
      'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=500&h=350&fit=crop',
      'https://images.unsplash.com/photo-1497366216548-37526070297c?w=500&h=350&fit=crop',
      'https://images.unsplash.com/photo-1577985043696-8bd54d9f9eed?w=500&h=350&fit=crop',
      'https://images.unsplash.com/photo-1554469384-e58fac16e23a?w=500&h=350&fit=crop',
      'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=500&h=350&fit=crop',
    ];
    const residentialImages = [
      'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=500&h=350&fit=crop',
      'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=500&h=350&fit=crop',
      'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=500&h=350&fit=crop',
      'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=500&h=350&fit=crop',
      'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=500&h=350&fit=crop',
    ];
    const vacationImages = [
      'https://images.unsplash.com/photo-1502672260266-6bc36a9d9c25?w=500&h=350&fit=crop',
      'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=500&h=350&fit=crop',
      'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=500&h=350&fit=crop',
      'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=500&h=350&fit=crop',
      'https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=500&h=350&fit=crop',
    ];

    // Location coordinates for different cities
    const cityCoords = {
      'Dallas': { lat: 32.7767, lng: -96.7970 },
      'Houston': { lat: 29.7604, lng: -95.3698 },
      'Austin': { lat: 30.2672, lng: -97.7431 },
      'San Antonio': { lat: 29.4241, lng: -98.4936 },
      'Fort Worth': { lat: 32.7555, lng: -97.3308 },
      'Los Angeles': { lat: 34.0522, lng: -118.2437 },
      'New York': { lat: 40.7128, lng: -74.0060 },
      'Miami': { lat: 25.7617, lng: -80.1918 },
      'Chicago': { lat: 41.8781, lng: -87.6298 },
      'Phoenix': { lat: 33.4484, lng: -112.0740 },
    };
    
    const baseCoords = cityCoords[cityName] || cityCoords['Dallas'];
    const getRandomCoords = () => ({
      lat: baseCoords.lat + (Math.random() - 0.5) * 0.2,
      lng: baseCoords.lng + (Math.random() - 0.5) * 0.2
    });

    // Neighborhoods for the searched city
    const neighborhoods = {
      'Dallas': ['Downtown', 'Uptown', 'Deep Ellum', 'Knox Henderson', 'Oak Lawn', 'Preston Hollow', 'Highland Park', 'Bishop Arts', 'Design District', 'Victory Park'],
      'Houston': ['Downtown', 'Midtown', 'Montrose', 'The Heights', 'River Oaks', 'Galleria', 'Memorial', 'West University', 'Museum District', 'EaDo'],
      'Austin': ['Downtown', 'South Congress', 'East Austin', 'Hyde Park', 'Zilker', 'Tarrytown', 'Mueller', 'Domain', 'Rainey Street', 'Clarksville'],
      'default': ['Downtown', 'Midtown', 'Uptown', 'Suburbs', 'Historic District', 'Arts District', 'Waterfront', 'Business District', 'University Area', 'Tech Corridor']
    };
    
    const cityNeighborhoods = neighborhoods[cityName] || neighborhoods['default'];
    
    // Street names for realistic addresses
    const streets = ['Main St', 'Commerce St', 'Market St', 'Oak Ave', 'Elm St', 'Park Blvd', 'Central Ave', 'First St', 'Broadway', 'Highland Dr', 'Lake View Dr', 'Industrial Blvd', 'Corporate Dr', 'Tech Way'];
    
    // Generate listings based on platform type
    const generateListings = (count, type) => {
      const listings = [];
      
      for (let i = 1; i <= count; i++) {
        const neighborhood = cityNeighborhoods[Math.floor(Math.random() * cityNeighborhoods.length)];
        const street = streets[Math.floor(Math.random() * streets.length)];
        const streetNum = Math.floor(Math.random() * 9000) + 100;
        
        if (type === 'commercial') {
          const commercialTypes = ['Commercial Office', 'Commercial Retail', 'Industrial', 'Mixed-Use'];
          const propType = commercialTypes[Math.floor(Math.random() * commercialTypes.length)];
          const names = {
            'Commercial Office': ['Office Tower', 'Business Center', 'Corporate Plaza', 'Executive Suites', 'Professional Building'],
            'Commercial Retail': ['Retail Center', 'Shopping Plaza', 'Strip Mall', 'Retail Complex', 'Town Center'],
            'Industrial': ['Distribution Center', 'Warehouse', 'Industrial Park', 'Flex Space', 'Manufacturing Facility'],
            'Mixed-Use': ['Mixed-Use Development', 'Urban Center', 'Live-Work Complex', 'Transit-Oriented Development']
          };
          const nameOptions = names[propType];
          const propName = nameOptions[Math.floor(Math.random() * nameOptions.length)];
          const price = propType === 'Industrial' ? (5 + Math.random() * 40) : (10 + Math.random() * 80);
          const area = propType === 'Industrial' ? (50000 + Math.random() * 200000) : (10000 + Math.random() * 150000);
          
          listings.push({
            id: `${platformId}-${i}-${Date.now()}`,
            source: platformId,
            name: `${neighborhood} ${propName}`,
            type: propType,
            address: `${streetNum} ${street}, ${cityName}, ${statePart}`,
            location: neighborhood,
            price: Math.round(price * 10) / 10,
            priceDisplay: `$${price.toFixed(1)}M`,
            listingType: 'sale',
            beds: 0, baths: 0,
            area: Math.round(area),
            occupancy: 85 + Math.floor(Math.random() * 15),
            capRate: 5.5 + Math.random() * 3,
            annualRevenue: price * 0.08,
            yearBuilt: 2010 + Math.floor(Math.random() * 14),
            rating: 4.2 + Math.random() * 0.8,
            hasWifi: true, hasParking: true, parking: Math.floor(area / 500),
            image: commercialImages[i % commercialImages.length],
            images: commercialImages,
            description: `Prime ${propType.toLowerCase()} property in ${neighborhood}. Excellent location with strong tenant mix and stable income.`,
            url: `https://${platformId}.com`,
            ...getRandomCoords()
          });
        } else if (type === 'residential') {
          const residentialTypes = ['House', 'Townhome', 'Condo', 'Penthouse'];
          const propType = residentialTypes[Math.floor(Math.random() * residentialTypes.length)];
          const beds = propType === 'Penthouse' ? (2 + Math.floor(Math.random() * 3)) : (2 + Math.floor(Math.random() * 5));
          const baths = beds + Math.floor(Math.random() * 2);
          const price = propType === 'Penthouse' ? (1.5 + Math.random() * 4) : (0.4 + Math.random() * 3);
          const area = beds * 600 + Math.random() * 2000;
          
          const styles = ['Modern', 'Contemporary', 'Traditional', 'Luxury', 'Renovated', 'New Construction'];
          const style = styles[Math.floor(Math.random() * styles.length)];
          
          listings.push({
            id: `${platformId}-${i}-${Date.now()}`,
            source: platformId,
            name: `${style} ${propType} in ${neighborhood}`,
            type: propType,
            address: `${streetNum} ${street}, ${cityName}, ${statePart}`,
            location: neighborhood,
            price: Math.round(price * 100) / 100,
            priceDisplay: price >= 1 ? `$${price.toFixed(2)}M` : `$${Math.round(price * 1000)}K`,
            listingType: 'sale',
            beds, baths,
            area: Math.round(area),
            yearBuilt: 2000 + Math.floor(Math.random() * 24),
            rating: 4.3 + Math.random() * 0.7,
            hasWifi: true, hasParking: true, parking: Math.min(beds, 4),
            image: residentialImages[i % residentialImages.length],
            images: residentialImages,
            description: `Beautiful ${beds}BR/${baths}BA ${propType.toLowerCase()} in desirable ${neighborhood}. ${style} finishes throughout.`,
            url: `https://${platformId}.com`,
            ...getRandomCoords()
          });
        } else if (type === 'vacation') {
          const vacationTypes = ['Loft', 'Apartment', 'House', 'Studio', 'Condo'];
          const propType = vacationTypes[Math.floor(Math.random() * vacationTypes.length)];
          const beds = propType === 'Studio' ? 0 : (1 + Math.floor(Math.random() * 3));
          const baths = Math.max(1, beds);
          const pricePerNight = 80 + Math.floor(Math.random() * 300);
          const area = 500 + beds * 400 + Math.random() * 500;
          
          const vibes = ['Stylish', 'Cozy', 'Luxury', 'Modern', 'Charming', 'Spacious', 'Hip', 'Urban'];
          const vibe = vibes[Math.floor(Math.random() * vibes.length)];
          
          listings.push({
            id: `${platformId}-${i}-${Date.now()}`,
            source: platformId,
            name: `${vibe} ${propType} - ${neighborhood}`,
            type: propType,
            address: `${neighborhood}, ${cityName}, ${statePart}`,
            location: neighborhood,
            price: pricePerNight / 30000,
            priceDisplay: `$${pricePerNight}/night`,
            listingType: 'lease',
            beds, baths,
            area: Math.round(area),
            yearBuilt: 2010 + Math.floor(Math.random() * 14),
            rating: 4.5 + Math.random() * 0.5,
            hasWifi: true, hasParking: Math.random() > 0.3, parking: Math.random() > 0.5 ? 1 : 0,
            image: vacationImages[i % vacationImages.length],
            images: vacationImages,
            description: `${vibe} ${propType.toLowerCase()} perfect for your ${cityName} getaway. ${beds > 0 ? `${beds}BR/` : ''}${baths}BA with all amenities.`,
            url: `https://${platformId}.com`,
            ...getRandomCoords()
          });
        }
      }
      return listings;
    };
    
    // Generate listings based on platform
    const platformConfig = {
      loopnet: { count: 15, type: 'commercial' },
      zillow: { count: 20, type: 'residential' },
      airbnb: { count: 18, type: 'vacation' },
      realtor: { count: 16, type: 'residential' },
      redfin: { count: 14, type: 'residential' },
      crexi: { count: 12, type: 'commercial' }
    };
    
    const config = platformConfig[platformId] || { count: 10, type: 'residential' };
    const newListings = generateListings(config.count, config.type);
    
    setExternalListings(prev => [...prev.filter(l => l.source !== platformId), ...newListings]);
    setIsLoadingListings(false);
    toast.success(`Found ${newListings.length} listings in ${cityName} from ${platformId}`);
  };

  // Refresh all connected listings
  const refreshAllListings = async (locationOverride = null) => {
    const connectedPlatforms = listingConnections.filter(c => c.connected);
    if (connectedPlatforms.length === 0) {
      toast.error('No platforms connected');
      return;
    }
    setIsLoadingListings(true);
    // Clear existing external listings before refreshing
    setExternalListings([]);
    for (const conn of connectedPlatforms) {
      await fetchExternalListings(conn.platform, conn.credentials, locationOverride);
    }
    setIsLoadingListings(false);
  };

  // Handle location search from the panel
  const handleSearchLocationChange = async (newLocation) => {
    setSearchLocation(newLocation);
    const connectedPlatforms = listingConnections.filter(c => c.connected);
    if (connectedPlatforms.length === 0) {
      toast.error('Connect a platform first to search listings');
      return;
    }
    toast.info(`Searching listings in ${newLocation}...`);
    await refreshAllListings(newLocation);
  };

  // Load listings for all previously connected platforms on mount
  useEffect(() => {
    const connectedPlatforms = listingConnections.filter(c => c.connected);
    if (connectedPlatforms.length > 0 && externalListings.length === 0) {
      const loadConnectedListings = async () => {
        setIsLoadingListings(true);
        for (const conn of connectedPlatforms) {
          await fetchExternalListings(conn.platform, conn.credentials);
        }
        setIsLoadingListings(false);
      };
      loadConnectedListings();
    }
  }, [listingConnections.length]); // Only re-run when number of connections changes
  
  // Filter states
  const [filters, setFilters] = useState({
    propertyType: 'All',
    minPrice: 0,
    maxPrice: 100,
    minBeds: 0,
    maxBeds: 10,
    minBaths: 0,
    hasWifi: false,
    hasParking: false,
  });

  // Mock property data with Dallas, Texas commercial properties for sale
  const allProperties = [
    {
      id: 1,
      name: 'Uptown Dallas Tower',
      type: 'Commercial Office',
      address: '2911 Turtle Creek Blvd, Uptown',
      location: 'Uptown',
      price: 47.5,
      beds: 0,
      baths: 0,
      hasWifi: true,
      hasParking: true,
      parking: 250,
      area: 125000,
      occupancy: 96,
      capRate: 6.2,
      annualRevenue: 3.8,
      yearBuilt: 2018,
      rating: 4.9,
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=500&h=350&fit=crop',
      images: [
        'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=800&h=600&fit=crop'
      ],
      description: 'Prime Class A office tower in Uptown Dallas featuring 125,000 sq ft of premium commercial space. Strong tenant mix with Fortune 500 companies, excellent location near major highways and DART stations. 96% occupied with stable, long-term leases. Strong cash flow with 6.2% cap rate and immediate value-add opportunities through amenity upgrades.',
      lat: 32.8067,
      lng: -96.8011
    },
    {
      id: 2,
      name: 'Knox Henderson Retail Center',
      type: 'Commercial Retail',
      address: '3110 Knox St, Knox Henderson',
      location: 'Knox Henderson',
      price: 18.5,
      beds: 0,
      baths: 0,
      hasWifi: true,
      hasParking: true,
      parking: 150,
      area: 45000,
      occupancy: 92,
      capRate: 7.1,
      annualRevenue: 1.5,
      yearBuilt: 2015,
      rating: 4.6,
      image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=500&h=350&fit=crop',
      images: [
        'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1555421689-491a97ff2040?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1567958451986-2de427a4a0be?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1519558260268-cde7e03a0152?w=800&h=600&fit=crop'
      ],
      description: 'High-traffic retail center in trendy Knox Henderson district. 45,000 sq ft with mix of national and local tenants including restaurants, boutiques, and services. Excellent demographics with high-income residential nearby. Strong foot traffic and visibility on Knox Street. Triple-net leases with annual escalations.',
      lat: 32.8156,
      lng: -96.7945
    },
    {
      id: 3,
      name: 'Deep Ellum Mixed-Use Development',
      type: 'Mixed-Use',
      address: '2600 Main St, Deep Ellum',
      location: 'Deep Ellum',
      price: 32.0,
      beds: 0,
      baths: 0,
      hasWifi: true,
      hasParking: true,
      parking: 180,
      area: 85000,
      occupancy: 94,
      capRate: 6.8,
      annualRevenue: 2.4,
      yearBuilt: 2019,
      rating: 4.7,
      image: 'https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=500&h=350&fit=crop',
      images: [
        'https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1502672260066-6bc36a9d9c25?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1574643156929-51fa098b0394?w=800&h=600&fit=crop'
      ],
      description: 'Cutting-edge mixed-use property in vibrant Deep Ellum arts district. Ground floor retail with luxury apartments above. Strong rent growth potential in rapidly appreciating neighborhood. Diverse tenant base including restaurants, galleries, and creative offices. Significant upside through lease-up of remaining space and rent increases to market rates.',
      lat: 32.7847,
      lng: -96.7809
    },
    {
      id: 4,
      name: 'Downtown Financial District Tower',
      type: 'Commercial Office',
      address: '1500 Main St, Downtown',
      location: 'Downtown',
      price: 65.0,
      beds: 0,
      baths: 0,
      hasWifi: true,
      hasParking: true,
      parking: 400,
      area: 185000,
      occupancy: 98,
      capRate: 5.9,
      annualRevenue: 4.2,
      yearBuilt: 2020,
      rating: 4.8,
      image: 'https://images.unsplash.com/photo-1577495508048-b635879837f1?w=500&h=350&fit=crop',
      images: [
        'https://images.unsplash.com/photo-1577495508048-b635879837f1?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=800&h=600&fit=crop'
      ],
      description: 'Premier Class A+ office tower in Dallas Financial District. 185,000 sq ft of premium space with 98% occupancy. Major financial institutions as tenants. LEED Platinum certified with state-of-the-art technology infrastructure. Excellent transportation access and amenities.',
      lat: 32.7816,
      lng: -96.7970
    },
    {
      id: 5,
      name: 'Design District Warehouse Complex',
      type: 'Commercial Warehouse',
      address: '1025 Dragon St, Design District',
      location: 'Design District',
      price: 28.5,
      beds: 0,
      baths: 0,
      hasWifi: true,
      hasParking: true,
      parking: 120,
      area: 95000,
      occupancy: 90,
      capRate: 7.5,
      annualRevenue: 2.3,
      yearBuilt: 2017,
      rating: 4.7,
      image: 'https://images.unsplash.com/photo-1553413077-190dd305871c?w=500&h=350&fit=crop',
      images: [
        'https://images.unsplash.com/photo-1553413077-190dd305871c?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1565008576549-57569a49371d?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&h=600&fit=crop'
      ],
      description: 'Modern industrial warehouse in booming Design District. 95,000 sq ft with high ceilings and flexible layout. Mix of creative office and warehouse users. Excellent access to major highways. Strong demand from tech, design, and e-commerce tenants. Significant value-add potential.',
      lat: 32.7943,
      lng: -96.8142
    },
    {
      id: 6,
      name: 'Legacy West Office Park',
      type: 'Commercial Office',
      address: '7700 Windrose Ave, Plano',
      location: 'Plano',
      price: 72.0,
      beds: 0,
      baths: 0,
      hasWifi: true,
      hasParking: true,
      parking: 500,
      area: 210000,
      occupancy: 95,
      capRate: 6.4,
      annualRevenue: 5.1,
      yearBuilt: 2016,
      rating: 4.9,
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=500&h=350&fit=crop',
      images: [
        'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=800&h=600&fit=crop'
      ],
      description: 'Corporate campus in prestigious Legacy West development. 210,000 sq ft Class A office space with Fortune 500 tenants. Premium amenities including conference center, fitness facility, and on-site dining. Excellent visibility and access. Strong cash flow with long-term leases.',
      lat: 33.0762,
      lng: -96.8353
    },
    {
      id: 7,
      name: 'Frisco Tech Campus',
      type: 'Commercial Office',
      address: '5755 Legacy Dr, Frisco',
      location: 'Frisco',
      price: 95.0,
      beds: 0,
      baths: 0,
      hasWifi: true,
      hasParking: true,
      parking: 650,
      area: 280000,
      occupancy: 97,
      capRate: 6.1,
      annualRevenue: 6.4,
      yearBuilt: 2019,
      rating: 4.9,
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=500&h=350&fit=crop',
      images: [
        'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=800&h=600&fit=crop'
      ],
      description: 'Flagship tech campus in rapidly growing Frisco. 280,000 sq ft purpose-built for technology companies. Multiple major tech tenants with 10+ year leases. World-class amenities and infrastructure. Located in Dallas-Fort Worth\'s fastest growing commercial corridor.',
      lat: 33.0838,
      lng: -96.8236
    },
    {
      id: 8,
      name: 'Victory Park Entertainment Complex',
      type: 'Mixed-Use',
      address: '2430 Victory Park Ln, Victory Park',
      location: 'Victory Park',
      price: 88.0,
      beds: 0,
      baths: 0,
      hasWifi: true,
      hasParking: true,
      parking: 800,
      area: 320000,
      occupancy: 93,
      capRate: 6.6,
      annualRevenue: 6.8,
      yearBuilt: 2015,
      rating: 4.8,
      image: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=500&h=350&fit=crop',
      images: [
        'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1502672260066-6bc36a9d9c25?w=800&h=600&fit=crop'
      ],
      description: 'Premier mixed-use entertainment destination adjacent to American Airlines Center. Retail, restaurants, residential, and entertainment venues. High foot traffic from sports and concerts. Diverse tenant mix including national brands. Strong value-add through lease renewals and amenity improvements.',
      lat: 32.7902,
      lng: -96.8101
    },
    {
      id: 9,
      name: 'Arts District Cultural Center',
      type: 'Commercial Retail',
      address: '1900 N Pearl St, Arts District',
      location: 'Arts District',
      price: 42.0,
      beds: 0,
      baths: 0,
      hasWifi: true,
      hasParking: true,
      parking: 220,
      area: 110000,
      occupancy: 91,
      capRate: 6.9,
      annualRevenue: 3.2,
      yearBuilt: 2018,
      rating: 4.6,
      image: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=500&h=350&fit=crop',
      images: [
        'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1555421689-491a97ff2040?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1567958451986-2de427a4a0be?w=800&h=600&fit=crop'
      ],
      description: 'Cultural retail center in Dallas Arts District near museums and performing arts venues. 110,000 sq ft with upscale restaurants, galleries, and specialty retail. High-income demographics and tourist traffic. Strong lease terms with credit tenants. Significant upside potential.',
      lat: 32.7886,
      lng: -96.8001
    },
    {
      id: 10,
      name: 'Addison Airport Business Park',
      type: 'Commercial Office',
      address: '15301 Dallas Pkwy, Addison',
      location: 'Addison',
      price: 38.5,
      beds: 0,
      baths: 0,
      hasWifi: true,
      hasParking: true,
      parking: 300,
      area: 95000,
      occupancy: 94,
      capRate: 7.0,
      annualRevenue: 2.9,
      yearBuilt: 2014,
      rating: 4.5,
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=500&h=350&fit=crop',
      images: [
        'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=800&h=600&fit=crop'
      ],
      description: 'Professional office park near Addison Airport. 95,000 sq ft with aviation and professional service tenants. Excellent access to DFW Airport and major highways. Below-market rents offer strong value-add opportunity. Stable tenant base with room for improvement.',
      lat: 32.9543,
      lng: -96.8289
    },
    {
      id: 11,
      name: 'Bishop Arts Mixed-Use Village',
      type: 'Mixed-Use',
      address: '420 N Bishop Ave, Oak Cliff',
      location: 'Oak Cliff',
      price: 24.0,
      beds: 0,
      baths: 0,
      hasWifi: true,
      hasParking: true,
      parking: 100,
      area: 55000,
      occupancy: 89,
      capRate: 7.3,
      annualRevenue: 1.9,
      yearBuilt: 2020,
      rating: 4.7,
      image: 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=500&h=350&fit=crop',
      images: [
        'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1502672260066-6bc36a9d9c25?w=800&h=600&fit=crop'
      ],
      description: 'Trendy mixed-use property in Bishop Arts District. Boutique retail below with residential units above. Hip neighborhood with strong growth trajectory. Local artisan tenants and creative businesses. Excellent opportunity in emerging market with significant appreciation potential.',
      lat: 32.7487,
      lng: -96.8217
    },
    {
      id: 12,
      name: 'Lakewood Shopping Plaza',
      type: 'Commercial Retail',
      address: '6335 Palo Pinto Ave, Lakewood',
      location: 'Lakewood',
      price: 31.5,
      beds: 0,
      baths: 0,
      hasWifi: true,
      hasParking: true,
      parking: 200,
      area: 72000,
      occupancy: 96,
      capRate: 7.2,
      annualRevenue: 2.5,
      yearBuilt: 2012,
      rating: 4.8,
      image: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=500&h=350&fit=crop',
      images: [
        'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1555421689-491a97ff2040?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1567958451986-2de427a4a0be?w=800&h=600&fit=crop'
      ],
      description: 'Neighborhood shopping center in affluent Lakewood area. 72,000 sq ft anchored by grocery store and national tenants. High-income demographics and strong traffic counts. Stable cash flow with long-term triple-net leases. Excellent location in established residential area.',
      lat: 32.8185,
      lng: -96.7611
    },
    {
      id: 13,
      name: 'Medical District Professional Building',
      type: 'Commercial Office',
      address: '3500 Harry Hines Blvd, Medical District',
      location: 'Medical District',
      price: 54.0,
      beds: 0,
      baths: 0,
      hasWifi: true,
      hasParking: true,
      parking: 350,
      area: 145000,
      occupancy: 99,
      capRate: 6.3,
      annualRevenue: 3.7,
      yearBuilt: 2017,
      rating: 4.9,
      image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=500&h=350&fit=crop',
      images: [
        'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=800&h=600&fit=crop'
      ],
      description: 'Medical office building adjacent to major hospitals. 145,000 sq ft with 99% occupancy by healthcare providers. Long-term leases with credit medical groups. Specialized build-out for medical use. Stable, recession-resistant tenant base. Prime location in growing Medical District.',
      lat: 32.8127,
      lng: -96.8353
    },
    {
      id: 14,
      name: 'Trinity Groves Restaurant District',
      type: 'Commercial Retail',
      address: '3011 Gulden Ln, Trinity Groves',
      location: 'West Dallas',
      price: 36.5,
      beds: 0,
      baths: 0,
      hasWifi: true,
      hasParking: true,
      parking: 280,
      area: 68000,
      occupancy: 88,
      capRate: 7.4,
      annualRevenue: 2.9,
      yearBuilt: 2014,
      rating: 4.6,
      image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=500&h=350&fit=crop',
      images: [
        'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1555421689-491a97ff2040?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1567958451986-2de427a4a0be?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1519558260268-cde7e03a0152?w=800&h=600&fit=crop'
      ],
      description: 'Unique restaurant and entertainment complex with Dallas skyline views. 68,000 sq ft featuring nationally-recognized chefs and concepts. High traffic from events and tourism. Significant upside through lease-up and event revenue. Located in rapidly developing West Dallas corridor.',
      lat: 32.7832,
      lng: -96.8245
    },
    {
      id: 15,
      name: 'Park Cities Office Complex',
      type: 'Commercial Office',
      address: '8115 Preston Rd, University Park',
      location: 'University Park',
      price: 44.5,
      beds: 0,
      baths: 0,
      hasWifi: true,
      hasParking: true,
      parking: 280,
      area: 105000,
      occupancy: 97,
      capRate: 6.5,
      annualRevenue: 3.2,
      yearBuilt: 2016,
      rating: 4.8,
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=500&h=350&fit=crop',
      images: [
        'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=800&h=600&fit=crop'
      ],
      description: 'Boutique office building in prestigious Park Cities. 105,000 sq ft with professional services tenants. Excellent location near SMU and Highland Park. High-quality tenant roster with strong credit. Well-maintained property with attractive rent growth potential.',
      lat: 32.8542,
      lng: -96.8015
    },
  ];

  // Get unique locations
  const locations = ['All Locations', ...new Set(allProperties.map(p => p.location))];

  // Combine all properties with external listings
  const combinedProperties = useMemo(() => {
    // If there are connected platforms with listings, show those
    // Otherwise show the default mock properties
    const hasConnectedListings = externalListings.length > 0;
    
    if (hasConnectedListings) {
      return externalListings;
    }
    return allProperties;
  }, [allProperties, externalListings]);

  // Filter properties based on all filter criteria
  const filteredProperties = useMemo(() => {
    return combinedProperties.filter(property => {
      // Category filter
      if (activeCategory !== 'All' && property.type !== activeCategory) {
        return false;
      }
      
      // Location filter
      if (locationFilter !== 'All Locations' && property.location !== locationFilter) {
        return false;
      }
      
      // Property type filter
      if (filters.propertyType !== 'All' && property.type !== filters.propertyType) {
        return false;
      }
      
      // Price range
      if (property.price < filters.minPrice || property.price > filters.maxPrice) {
        return false;
      }
      
      // Bedroom range
      if (property.beds < filters.minBeds || property.beds > filters.maxBeds) {
        return false;
      }
      
      // Bathroom range
      if (property.baths < filters.minBaths) {
        return false;
      }
      
      // Amenities
      if (filters.hasWifi && !property.hasWifi) {
        return false;
      }
      
      if (filters.hasParking && !property.hasParking) {
        return false;
      }
      
      return true;
    });
  }, [locationFilter, filters, combinedProperties, activeCategory]);

  const handlePropertyClick = (property) => {
    setSelectedProperty(property.id);
  };

  const handleMarkerClick = (property) => {
    setSelectedProperty(property.id);
    // Scroll to the property card
    const element = document.getElementById(`property-${property.id}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const resetFilters = () => {
    setFilters({
      propertyType: 'All',
      minPrice: 0,
      maxPrice: 100,
      minBeds: 0,
      maxBeds: 10,
      minBaths: 0,
      hasWifi: false,
      hasParking: false,
    });
    setLocationFilter('All Locations');
  };

  return (
    <div className="h-screen bg-black flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-black border-b border-gray-400 px-3 md:px-6 py-3 md:py-4 flex-shrink-0 hover:border-gray-300 transition-all duration-300">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <h1 className="text-xl md:text-3xl font-bold text-white">Properties</h1>
          
          <div className="flex items-center gap-2 md:gap-3 flex-wrap w-full md:w-auto">
            {/* Location Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowLocationDropdown(!showLocationDropdown)}
                className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-black border border-gray-400 rounded-lg hover:border-gray-400 transition-colors"
              >
                <MapPin size={16} className="text-gray-400" />
                <span className="text-xs md:text-sm text-white truncate max-w-[80px] md:max-w-none">{locationFilter}</span>
                <ChevronDown size={14} className="text-gray-400" />
              </button>
              
              {/* Location Dropdown Menu */}
              {showLocationDropdown && (
                <div className="absolute top-full left-0 mt-2 bg-black border border-gray-400 rounded-lg p-2 z-50 min-w-[180px] md:min-w-[200px]">
                  {locations.map((location) => (
                    <button
                      key={location}
                      onClick={() => {
                        setLocationFilter(location);
                        setShowLocationDropdown(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded text-xs md:text-sm hover:bg-white/10 transition-colors ${
                        locationFilter === location ? 'bg-white text-black' : 'text-white'
                      }`}
                    >
                      {location}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Grid/List Toggle - Hidden on mobile */}
            <div className="hidden md:flex items-center gap-1 bg-gray-900 rounded-lg p-1">
              <button
                onClick={() => setViewType('grid')}
                className={`p-2 rounded-lg transition-all ${
                  viewType === 'grid' ? 'bg-white text-black' : 'text-gray-400 hover:text-white'
                }`}
              >
                <Grid size={20} />
              </button>
              <button
                onClick={() => setViewType('list')}
                className={`p-2 rounded-lg transition-all ${
                  viewType === 'list' ? 'bg-white text-black' : 'text-gray-400 hover:text-white'
                }`}
              >
                <List size={20} />
              </button>
            </div>

            {/* Filter Button */}
            <button
              onClick={() => setShowFilterMenu(!showFilterMenu)}
              className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-white text-black rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Filter size={16} />
              <span className="text-xs md:text-sm font-semibold">Filter</span>
            </button>

            {/* Add New Properties Button */}
            <button 
              onClick={() => setShowAddPropertyModal(true)}
              className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-white text-black rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Plus size={16} />
              <span className="text-xs md:text-sm font-semibold hidden sm:inline">Add New</span>
              <span className="text-xs font-semibold sm:hidden">Add</span>
            </button>

            {/* View Map Toggle */}
            <label className="flex items-center gap-1.5 md:gap-2 cursor-pointer">
              <span className="text-xs md:text-sm text-white">Map</span>
              <div
                onClick={() => setShowMapView(!showMapView)}
                className={`w-10 md:w-12 h-5 md:h-6 rounded-full transition-colors ${
                  showMapView ? 'bg-teal-500' : 'bg-gray-600'
                }`}
              >
                <div
                  className={`w-4 md:w-5 h-4 md:h-5 bg-white rounded-full shadow-md transform transition-transform ${
                    showMapView ? 'translate-x-5 md:translate-x-6' : 'translate-x-1'
                  } mt-0.5`}
                />
              </div>
            </label>

            {/* 3D/2D Toggle Button - Hidden on mobile */}
            {showMapView && (
              <button
                onClick={() => setMap3DMode(!map3DMode)}
                className={`hidden md:flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  map3DMode
                    ? 'bg-white text-black'
                    : 'bg-black border border-gray-400 text-white hover:bg-white/10'
                }`}
              >
                <Box size={18} />
                <span className="text-sm font-semibold">{map3DMode ? '2D View' : '3D View'}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilterMenu && (
        <div className="bg-black border-b border-gray-400 px-3 md:px-6 py-3 md:py-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <div>
              <label className="text-[10px] md:text-xs text-gray-400 mb-1 block">Property Type</label>
              <select
                value={filters.propertyType}
                onChange={(e) => setFilters({ ...filters, propertyType: e.target.value })}
                className="w-full px-2 md:px-3 py-1.5 md:py-2 bg-black border border-gray-400 text-white rounded-lg text-xs md:text-sm"
              >
                <option>All</option>
                <option>House</option>
                <option>Apartment</option>
                <option>Condo</option>
                <option>Loft</option>
                <option>Penthouse</option>
                <option>Commercial Office</option>
                <option>Commercial Retail</option>
                <option>Commercial Warehouse</option>
              </select>
            </div>
            
            <div>
              <label className="text-[10px] md:text-xs text-gray-400 mb-1 block">Price Range</label>
              <div className="flex gap-1.5 md:gap-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.minPrice}
                  onChange={(e) => setFilters({ ...filters, minPrice: parseInt(e.target.value) || 0 })}
                  className="w-1/2 px-2 md:px-3 py-1.5 md:py-2 bg-black border border-gray-400 text-white rounded-lg text-xs md:text-sm"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.maxPrice}
                  onChange={(e) => setFilters({ ...filters, maxPrice: parseInt(e.target.value) || 25000 })}
                  className="w-1/2 px-2 md:px-3 py-1.5 md:py-2 bg-black border border-gray-400 text-white rounded-lg text-xs md:text-sm"
                />
              </div>
            </div>
            
            <div className="hidden md:block">
              <label className="text-xs text-gray-400 mb-1 block">Bedrooms</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.minBeds}
                  onChange={(e) => setFilters({ ...filters, minBeds: parseInt(e.target.value) || 0 })}
                  className="w-1/2 px-3 py-2 bg-black border border-gray-400 text-white rounded-lg text-sm"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.maxBeds}
                  onChange={(e) => setFilters({ ...filters, maxBeds: parseInt(e.target.value) || 10 })}
                  className="w-1/2 px-3 py-2 bg-black border border-gray-400 text-white rounded-lg text-sm"
                />
              </div>
            </div>
            
            <div className="hidden md:block">
              <label className="text-xs text-gray-400 mb-1 block">Amenities</label>
              <div className="flex gap-3">
                <label className="flex items-center gap-1 text-sm text-white">
                  <input
                    type="checkbox"
                    checked={filters.hasWifi}
                    onChange={(e) => setFilters({ ...filters, hasWifi: e.target.checked })}
                    className="rounded"
                  />
                  <span>WiFi</span>
                </label>
                <label className="flex items-center gap-1 text-sm text-white">
                  <input
                    type="checkbox"
                    checked={filters.hasParking}
                    onChange={(e) => setFilters({ ...filters, hasParking: e.target.checked })}
                    className="rounded"
                  />
                  <span>Parking</span>
                </label>
              </div>
              <button
                onClick={resetFilters}
                className="text-xs text-teal-500 hover:text-teal-400 mt-2"
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Listing Connections Panel */}
      <div className="px-3 md:px-6 py-2">
        <ListingConnectionsPanel
          connections={listingConnections}
          onConnect={handleConnectListingPlatform}
          onDisconnect={handleDisconnectListingPlatform}
          isExpanded={listingConnectionsExpanded}
          onToggle={() => setListingConnectionsExpanded(!listingConnectionsExpanded)}
          listings={externalListings}
          onRefresh={refreshAllListings}
          isLoading={isLoadingListings}
          searchLocation={searchLocation}
          setSearchLocation={setSearchLocation}
          onSearchLocationChange={handleSearchLocationChange}
        />
      </div>

      {/* Main Content */}
      <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
        {/* Property List Sidebar */}
        <div className={`${showMapView ? 'w-full md:w-2/5' : 'w-full'} bg-black overflow-y-auto p-3 md:p-4`}>
          <div className="text-xs md:text-sm text-gray-400 mb-3 md:mb-4 flex items-center justify-between">
            <span>
              Showing {filteredProperties.length} {externalListings.length > 0 ? 'listings' : 'properties'}
              {externalListings.length > 0 && (
                <span className="ml-2 text-cyan-400">
                  from {[...new Set(externalListings.map(l => l.source))].join(', ')}
                </span>
              )}
            </span>
            {externalListings.length === 0 && (
              <span className="text-gray-500 text-[10px]">Connect a platform to see listings</span>
            )}
          </div>
          
          {/* Category Tabs */}
          <div className="mb-4 md:mb-6 flex items-center gap-1.5 md:gap-2 overflow-x-auto pb-2">
            {['All', 'Commercial Office', 'Commercial Retail', 'House', 'Apartment', 'Loft', 'Industrial'].map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-2 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-semibold whitespace-nowrap transition-all ${
                  activeCategory === category
                    ? 'bg-white text-black'
                    : 'bg-white/10 text-gray-300 hover:bg-white/20'
                }`}
              >
                {category === 'Commercial Office' ? 'Office' : category === 'Commercial Retail' ? 'Retail' : category}
              </button>
            ))}
          </div>
          
          {/* List View */}
          {viewType === 'list' && (
            <div className="space-y-3 md:space-y-4">
              {filteredProperties.map((property) => (
                <div
                  key={property.id}
                  id={`property-${property.id}`}
                  onClick={() => {
                    handlePropertyClick(property);
                    setDetailViewProperty(property);
                  }}
                  onMouseEnter={() => setHoveredProperty(property.id)}
                  onMouseLeave={() => setHoveredProperty(null)}
                  className={`bg-black rounded-xl md:rounded-2xl overflow-hidden border border-gray-400 shadow-md hover:shadow-xl transition-all cursor-pointer ${
                    selectedProperty === property.id ? 'ring-2 ring-teal-500' : ''
                  }`}
                >
                  <div className="flex flex-col sm:flex-row gap-3 md:gap-4 p-3 md:p-4">
                    {/* Property Image */}
                    <div className="relative w-full sm:w-40 md:w-48 h-36 sm:h-28 md:h-32 flex-shrink-0">
                      <img
                        src={property.image}
                        alt={property.name}
                        className="w-full h-full object-cover rounded-lg md:rounded-xl"
                      />
                      <div className="absolute top-2 left-2 bg-amber-100 text-amber-800 px-1.5 md:px-2 py-0.5 md:py-1 rounded-lg text-[10px] md:text-xs font-semibold">
                        {property.type.replace('Commercial ', '')}
                      </div>
                      <div className="absolute top-2 right-2 bg-black bg-opacity-80 px-1.5 md:px-2 py-0.5 md:py-1 rounded-lg flex items-center gap-1">
                        <Star size={10} className="fill-yellow-400 text-yellow-400" />
                        <span className="text-[10px] md:text-xs font-semibold text-white">{property.rating}</span>
                      </div>
                      {property.source && (
                        <div className="absolute bottom-2 left-2 bg-cyan-500/90 text-white px-1.5 py-0.5 rounded text-[9px] font-medium uppercase">
                          {property.source}
                        </div>
                      )}
                      {property.listingType && (
                        <div className={`absolute bottom-2 right-2 px-1.5 py-0.5 rounded text-[9px] font-medium ${
                          property.listingType === 'sale' ? 'bg-green-500/90 text-white' : 'bg-blue-500/90 text-white'
                        }`}>
                          {property.listingType === 'sale' ? 'For Sale' : 'For Lease'}
                        </div>
                      )}
                    </div>

                    {/* Property Details */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-white text-sm md:text-base mb-1 truncate">{property.name}</h3>
                      <div className="flex items-center gap-1 text-[10px] md:text-xs text-gray-400 mb-2 md:mb-3">
                        <MapPin size={10} />
                        <span className="truncate">{property.address}</span>
                      </div>

                      {/* Amenities */}
                      <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3 flex-wrap">
                        {property.beds > 0 && (
                          <div className="flex items-center gap-1 text-[10px] md:text-xs text-gray-300">
                            <Bed size={12} className="text-teal-500" />
                            <span>{property.beds} Beds</span>
                          </div>
                        )}
                        {property.baths > 0 && (
                          <div className="flex items-center gap-1 text-[10px] md:text-xs text-gray-300">
                            <Bath size={12} className="text-teal-500" />
                            <span>{property.baths} Bath</span>
                          </div>
                        )}
                        {property.hasWifi && (
                          <div className="flex items-center gap-1 text-[10px] md:text-xs text-gray-300">
                            <Wifi size={12} className="text-teal-500" />
                          </div>
                        )}
                        {property.hasParking && (
                          <div className="flex items-center gap-1 text-[10px] md:text-xs text-gray-300">
                            <Car size={12} className="text-teal-500" />
                          </div>
                        )}
                      </div>

                      {/* Price and Button */}
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-base md:text-xl font-bold text-white">${property.price}M</span>
                          <span className="text-[10px] md:text-sm text-gray-400 hidden sm:inline"> Sale Price</span>
                        </div>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setDetailViewProperty(property);
                          }}
                          className="px-3 md:px-4 py-1.5 md:py-2 bg-white text-black rounded-lg text-xs md:text-sm font-semibold hover:bg-gray-200 transition-colors"
                        >
                          Detail
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Grid View */}
          {viewType === 'grid' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              {filteredProperties.map((property) => (
                <div
                  key={property.id}
                  id={`property-${property.id}`}
                  onClick={() => {
                    handlePropertyClick(property);
                    setDetailViewProperty(property);
                  }}
                  onMouseEnter={() => setHoveredProperty(property.id)}
                  onMouseLeave={() => setHoveredProperty(null)}
                  className={`bg-black rounded-xl md:rounded-2xl overflow-hidden border border-gray-400 shadow-md hover:shadow-xl transition-all cursor-pointer ${
                    selectedProperty === property.id ? 'ring-2 ring-teal-500' : ''
                  }`}
                >
                  {/* Property Image */}
                  <div className="relative w-full h-32 md:h-40">
                    <img
                      src={property.image}
                      alt={property.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 left-2 bg-amber-100 text-amber-800 px-1.5 md:px-2 py-0.5 md:py-1 rounded-lg text-[10px] md:text-xs font-semibold">
                      {property.type.replace('Commercial ', '')}
                    </div>
                    <div className="absolute top-2 right-2 bg-black bg-opacity-80 px-1.5 md:px-2 py-0.5 md:py-1 rounded-lg flex items-center gap-1">
                      <Star size={10} className="fill-yellow-400 text-yellow-400" />
                      <span className="text-[10px] md:text-xs font-semibold text-white">{property.rating}</span>
                    </div>
                  </div>

                  {/* Property Details */}
                  <div className="p-3 md:p-4">
                    <h3 className="font-bold text-white mb-1 text-xs md:text-sm truncate">{property.name}</h3>
                    <div className="flex items-center gap-1 text-[10px] md:text-xs text-gray-400 mb-2">
                      <MapPin size={10} />
                      <span className="truncate">{property.location}</span>
                    </div>

                    {/* Price */}
                    <div className="mb-2 md:mb-3">
                      <span className="text-base md:text-lg font-bold text-white">${property.price}M</span>
                    </div>

                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setDetailViewProperty(property);
                      }}
                      className="w-full px-2 md:px-3 py-1.5 md:py-2 bg-white text-black rounded-lg text-[10px] md:text-xs font-semibold hover:bg-gray-200 transition-colors"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Map Section or Detail View - Hidden on mobile when showing list */}
        {showMapView && !detailViewProperty && !isMobile && (
          <div className="hidden md:block w-3/5 relative p-6">
            <div className="border-2 border-gray-400 rounded-2xl p-4 h-full bg-black overflow-hidden hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
              {!map3DMode ? (
                // 2D Map View (Leaflet)
                <MapContainer
                  center={[32.7767, -96.7970]}
                  zoom={11}
                  style={{ height: '100%', width: '100%', borderRadius: '12px' }}
                  className="z-0"
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapController searchLocation={searchLocation} />
                  
                  {filteredProperties.map((property) => (
                    <Marker
                      key={property.id}
                      position={[property.lat, property.lng]}
                      icon={selectedProperty === property.id || hoveredProperty === property.id ? selectedIcon : new L.Icon.Default()}
                      eventHandlers={{
                        click: () => handleMarkerClick(property),
                      }}
                    >
                      <Popup>
                        <div className="w-48">
                          <img
                            src={property.image}
                            alt={property.name}
                            className="w-full h-24 object-cover rounded-lg mb-2"
                          />
                          <div className="flex items-center gap-1 mb-1">
                            <div className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded text-xs font-semibold">
                              {property.type}
                            </div>
                            <div className="flex items-center gap-1">
                              <Star size={12} className="fill-yellow-400 text-yellow-400" />
                              <span className="text-xs font-semibold">{property.rating}</span>
                            </div>
                          </div>
                          <h3 className="font-bold text-sm mb-1">{property.name}</h3>
                          <p className="text-xs text-gray-600 mb-2">{property.address}</p>
                          <div className="flex items-center gap-2 mb-2">
                            <div className="flex items-center gap-1 text-xs">
                              <Bed size={12} className="text-teal-500" />
                              <span>{property.beds}</span>
                            </div>
                            <div className="flex items-center gap-1 text-xs">
                              <Bath size={12} className="text-teal-500" />
                              <span>{property.baths}</span>
                            </div>
                            {property.hasWifi && <Wifi size={12} className="text-teal-500" />}
                          </div>
                          <div className="text-sm font-bold">
                            ${property.price}M <span className="text-xs text-gray-500">Sale Price</span>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              ) : (
                // 3D Map View (Mapbox)
                <div style={{ height: '100%', width: '100%', borderRadius: '12px' }}>
                  <Map3DView
                    properties={filteredProperties}
                    onPropertySelect={handleMarkerClick}
                    selectedProperty={selectedProperty}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Property Detail View */}
        {detailViewProperty && (
          <div className="fixed inset-0 md:static md:w-3/5 bg-black overflow-y-auto p-4 md:p-6 z-[100]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl md:text-2xl font-bold text-white truncate pr-4">{detailViewProperty.name}</h2>
              <button
                onClick={() => setDetailViewProperty(null)}
                className="text-white hover:text-gray-400 text-2xl p-2 -mr-2"
                data-testid="close-detail-btn"
              >
                ×
              </button>
            </div>

            {/* Image Gallery - Stack on mobile */}
            <div className="flex flex-col md:flex-row gap-3 md:gap-4 mb-4 md:mb-6">
              {/* Main Image */}
              <div className="flex-1">
                <img
                  src={detailViewProperty.images?.[0] || detailViewProperty.image}
                  alt={detailViewProperty.name}
                  className="w-full h-48 md:h-96 object-cover rounded-xl"
                />
              </div>
              
              {/* Thumbnail Images - Horizontal scroll on mobile */}
              <div className="flex md:flex-col gap-2 md:gap-4 md:w-32 overflow-x-auto md:overflow-visible">
                {detailViewProperty.images?.slice(1, 4).map((img, idx) => (
                  <img
                    key={idx}
                    src={img}
                    alt={`${detailViewProperty.name} ${idx + 2}`}
                    className="w-20 h-16 md:w-full md:h-28 object-cover rounded-xl cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
                  />
                ))}
              </div>
            </div>

            {/* Property Info */}
            <div className="mb-4 md:mb-6">
              <div className="flex items-center gap-2 mb-2">
                <MapPin size={16} className="text-gray-400 flex-shrink-0" />
                <span className="text-gray-300 text-sm md:text-base">{detailViewProperty.address}</span>
              </div>
              <div className="flex items-center gap-1 mb-3 md:mb-4">
                <Star size={16} className="fill-yellow-400 text-yellow-400" />
                <span className="text-white font-semibold">{detailViewProperty.rating}</span>
              </div>
              
              <div className="text-2xl md:text-3xl font-bold text-white mb-2">
                ${detailViewProperty.price}M
              </div>
              <div className="text-xs md:text-sm text-gray-400 mb-4 md:mb-6">
                {detailViewProperty.capRate && `${detailViewProperty.capRate}% Cap Rate`}
                {detailViewProperty.annualRevenue && ` • $${detailViewProperty.annualRevenue}M Annual Revenue`}
              </div>
            </div>

            {/* Property Stats - 2 cols on mobile, 4 on desktop */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
              {detailViewProperty.area && (
                <div className="bg-black border border-gray-400 rounded-xl p-3 md:p-4 text-center hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
                  <svg className="w-5 h-5 md:w-6 md:h-6 text-teal-500 mx-auto mb-1 md:mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                  <div className="text-lg md:text-2xl font-bold text-white">{detailViewProperty.area.toLocaleString()}</div>
                  <div className="text-[10px] md:text-xs text-gray-400">Sq Ft</div>
                </div>
              )}
              
              {detailViewProperty.occupancy && (
                <div className="bg-black border border-gray-400 rounded-xl p-3 md:p-4 text-center">
                  <CheckCircle size={20} className="text-teal-500 mx-auto mb-1 md:mb-2 md:w-6 md:h-6" />
                  <div className="text-lg md:text-2xl font-bold text-white">{detailViewProperty.occupancy}%</div>
                  <div className="text-[10px] md:text-xs text-gray-400">Occupied</div>
                </div>
              )}
              
              {detailViewProperty.hasParking && detailViewProperty.parking && (
                <div className="bg-black border border-gray-400 rounded-xl p-3 md:p-4 text-center">
                  <Car size={20} className="text-teal-500 mx-auto mb-1 md:mb-2 md:w-6 md:h-6" />
                  <div className="text-lg md:text-2xl font-bold text-white">{detailViewProperty.parking}</div>
                  <div className="text-[10px] md:text-xs text-gray-400">Parking Spaces</div>
                </div>
              )}
              
              {detailViewProperty.yearBuilt && (
                <div className="bg-black border border-gray-400 rounded-xl p-3 md:p-4 text-center">
                  <Building2 size={20} className="text-teal-500 mx-auto mb-1 md:mb-2 md:w-6 md:h-6" />
                  <div className="text-lg md:text-2xl font-bold text-white">{detailViewProperty.yearBuilt}</div>
                  <div className="text-[10px] md:text-xs text-gray-400">Year Built</div>
                </div>
              )}
            </div>

            {/* Property Details */}
            <div className="bg-black border border-gray-400 rounded-xl p-4 md:p-6 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
              <h3 className="text-lg md:text-xl font-bold text-white mb-2 md:mb-3">Investment Overview</h3>
              <p className="text-gray-300 text-sm md:text-base leading-relaxed">
                {detailViewProperty.description || 'No description available.'}
              </p>
            </div>
          </div>
        )}
      </div>
      
      {/* Add New Property Modal */}
      {showAddPropertyModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-black border border-gray-400 rounded-xl p-4 md:p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4 md:mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-white">Add New Property</h2>
              <button
                onClick={() => setShowAddPropertyModal(false)}
                className="text-white hover:text-gray-400 text-2xl p-2 -mr-2"
              >
                ×
              </button>
            </div>

            <form className="space-y-3 md:space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Property Name</label>
                  <input
                    type="text"
                    placeholder="Enter property name"
                    className="w-full px-3 py-2 bg-black border border-gray-400 text-white rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Property Type</label>
                  <select className="w-full px-3 py-2 bg-black border border-gray-400 text-white rounded-lg text-sm">
                    <option>Commercial Office</option>
                    <option>Commercial Retail</option>
                    <option>Commercial Warehouse</option>
                    <option>Mixed-Use</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Address</label>
                <input
                  type="text"
                  placeholder="Enter full address"
                  className="w-full px-3 py-2 bg-black border border-gray-400 text-white rounded-lg text-sm"
                />
              </div>

              <div className="grid grid-cols-3 gap-2 md:gap-4">
                <div>
                  <label className="block text-xs md:text-sm text-gray-400 mb-1">Sale Price ($M)</label>
                  <input
                    type="number"
                    placeholder="0"
                    className="w-full px-2 md:px-3 py-2 bg-black border border-gray-400 text-white rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs md:text-sm text-gray-400 mb-1">Square Footage</label>
                  <input
                    type="number"
                    placeholder="0"
                    className="w-full px-2 md:px-3 py-2 bg-black border border-gray-400 text-white rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs md:text-sm text-gray-400 mb-1">Cap Rate (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="0.0"
                    className="w-full px-2 md:px-3 py-2 bg-black border border-gray-400 text-white rounded-lg text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 md:gap-4">
                <div>
                  <label className="block text-xs md:text-sm text-gray-400 mb-1">Occupancy (%)</label>
                  <input
                    type="number"
                    placeholder="0"
                    className="w-full px-2 md:px-3 py-2 bg-black border border-gray-400 text-white rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs md:text-sm text-gray-400 mb-1">Parking</label>
                  <input
                    type="number"
                    placeholder="0"
                    className="w-full px-2 md:px-3 py-2 bg-black border border-gray-400 text-white rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs md:text-sm text-gray-400 mb-1">Year Built</label>
                  <input
                    type="number"
                    placeholder="2020"
                    className="w-full px-2 md:px-3 py-2 bg-black border border-gray-400 text-white rounded-lg text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Description</label>
                <textarea
                  rows="3"
                  placeholder="Enter property description..."
                  className="w-full px-3 py-2 bg-black border border-gray-400 text-white rounded-lg text-sm"
                ></textarea>
              </div>

              <div className="flex gap-3 pt-3 md:pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddPropertyModal(false)}
                  className="flex-1 px-4 py-2.5 md:py-3 bg-black border border-gray-400 text-white rounded-lg font-semibold hover:bg-white/10 transition-colors text-sm md:text-base"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    alert('Property submission would be processed here. This is a demo.');
                    setShowAddPropertyModal(false);
                  }}
                  className="flex-1 px-4 py-2.5 md:py-3 bg-white text-black rounded-lg font-semibold hover:bg-gray-200 transition-colors text-sm md:text-base"
                >
                  Add Property
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RealEstateManagingPartnerProperties;
